import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  BellOff, 
  MessageSquare, 
  Briefcase, 
  DollarSign,
  CheckCircle,
  Loader2,
  Smartphone,
  Send,
  AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
  Alert,
  AlertDescription,
} from './ui/alert';
import { toast } from 'sonner';
import { pushAPI } from '../api/api';

// Helper to convert base64 to Uint8Array for push subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [testingNotification, setTestingNotification] = useState(false);
  const [status, setStatus] = useState({
    subscribed: false,
    enabled: true,
    categories: {
      messages: true,
      jobs: true,
      payments: true
    }
  });
  const [permissionState, setPermissionState] = useState('default');
  const [swSupported, setSwSupported] = useState(true);

  useEffect(() => {
    checkPushSupport();
    fetchStatus();
  }, []);

  const checkPushSupport = () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSwSupported(false);
      return;
    }
    
    if ('Notification' in window) {
      setPermissionState(Notification.permission);
    }
  };

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await pushAPI.getStatus();
      if (response.data.success) {
        setStatus(response.data.status);
      }
    } catch (error) {
      console.error('Failed to fetch push status:', error);
    } finally {
      setLoading(false);
    }
  };

  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registered:', registration);
        return registration;
      } catch (error) {
        console.error('Service Worker registration failed:', error);
        throw error;
      }
    }
    throw new Error('Service Worker not supported');
  };

  const subscribeToPush = async () => {
    setSubscribing(true);
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      setPermissionState(permission);
      
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        return;
      }

      // Register service worker
      const registration = await registerServiceWorker();
      
      // Get VAPID public key
      const keyResponse = await pushAPI.getVapidKey();
      const vapidPublicKey = keyResponse.data.publicKey;
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      await pushAPI.subscribe({
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });
      
      toast.success('Push notifications enabled!');
      fetchStatus();
    } catch (error) {
      console.error('Failed to subscribe to push:', error);
      toast.error('Failed to enable push notifications');
    } finally {
      setSubscribing(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setSubscribing(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();
        
        // Remove from backend
        const subscriptionJson = subscription.toJSON();
        await pushAPI.unsubscribe({
          endpoint: subscriptionJson.endpoint,
          keys: subscriptionJson.keys
        });
      }
      
      toast.success('Push notifications disabled');
      fetchStatus();
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      toast.error('Failed to disable push notifications');
    } finally {
      setSubscribing(false);
    }
  };

  const updatePreferences = async (newPrefs) => {
    try {
      const updated = {
        enabled: newPrefs.enabled ?? status.enabled,
        categories: { ...status.categories, ...newPrefs.categories }
      };
      
      await pushAPI.updatePreferences(updated);
      setStatus(prev => ({ ...prev, ...updated }));
      toast.success('Preferences updated');
    } catch (error) {
      console.error('Failed to update preferences:', error);
      toast.error('Failed to update preferences');
    }
  };

  const sendTestNotification = async () => {
    setTestingNotification(true);
    try {
      await pushAPI.sendTest();
      toast.success('Test notification sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setTestingNotification(false);
    }
  };

  if (!swSupported) {
    return (
      <Alert className="bg-amber-50 border-amber-200">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-700">
          Push notifications are not supported in your browser. Please use a modern browser like Chrome, Firefox, or Edge.
        </AlertDescription>
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
            <Bell className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Push Notifications
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Get instant alerts on your device
            </p>
          </div>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
          status.subscribed 
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
        }`}>
          {status.subscribed ? (
            <>
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Active</span>
            </>
          ) : (
            <>
              <BellOff className="w-4 h-4" />
              <span className="text-sm font-medium">Disabled</span>
            </>
          )}
        </div>
      </div>

      {/* Permission Warning */}
      {permissionState === 'denied' && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-700">
            Notifications are blocked in your browser. Please enable them in your browser settings to receive push notifications.
          </AlertDescription>
        </Alert>
      )}

      {/* Subscription Card */}
      <div className={`p-6 rounded-xl border ${
        status.subscribed 
          ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
          : 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
      }`}>
        {status.subscribed ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Smartphone className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-300">
                  Push notifications are enabled
                </p>
                <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                  You'll receive instant notifications for messages, job updates, and payments.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={sendTestNotification}
                disabled={testingNotification}
                className="border-green-300 text-green-700 hover:bg-green-100"
                data-testid="send-test-notification-btn"
              >
                {testingNotification ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Test
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={unsubscribeFromPush}
                disabled={subscribing}
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid="disable-push-btn"
              >
                {subscribing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <BellOff className="w-4 h-4 mr-2" />
                )}
                Disable
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800 dark:text-blue-300">
                  Enable push notifications
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  Stay updated with instant alerts for new messages, job applications, and payment updates.
                </p>
              </div>
            </div>
            <Button 
              onClick={subscribeToPush}
              disabled={subscribing || permissionState === 'denied'}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              data-testid="enable-push-btn"
            >
              {subscribing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Bell className="w-4 h-4 mr-2" />
              )}
              Enable Push Notifications
            </Button>
          </div>
        )}
      </div>

      {/* Category Preferences */}
      {status.subscribed && (
        <div className="p-6 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
            Notification Categories
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Choose which types of notifications you want to receive
          </p>
          
          <div className="space-y-4">
            {/* Messages */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Messages</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">New messages and replies</p>
                </div>
              </div>
              <Switch
                checked={status.categories.messages}
                onCheckedChange={(checked) => updatePreferences({ categories: { messages: checked } })}
                data-testid="messages-toggle"
              />
            </div>

            {/* Jobs */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Jobs</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Applications, matches, and updates</p>
                </div>
              </div>
              <Switch
                checked={status.categories.jobs}
                onCheckedChange={(checked) => updatePreferences({ categories: { jobs: checked } })}
                data-testid="jobs-toggle"
              />
            </div>

            {/* Payments */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Payments</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Escrow, invoices, and transactions</p>
                </div>
              </div>
              <Switch
                checked={status.categories.payments}
                onCheckedChange={(checked) => updatePreferences({ categories: { payments: checked } })}
                data-testid="payments-toggle"
              />
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <strong>Note:</strong> Push notifications work on both desktop and mobile browsers. 
          Make sure to allow notifications when prompted by your browser.
        </p>
      </div>
    </div>
  );
};

export default PushNotificationSettings;
